(() => {
  const ROOT_SELECTOR = '[data-lk-wholesale-catalog]';

  class LuckichWholesaleCatalog {
    constructor(root) {
      this.root = root;
      this.catalog = this.readJson('[data-wholesale-catalog-data]', []);
      this.variants = this.readJson('[data-wholesale-variant-data]', {});
      this.fallbackPrice = Number(root.dataset.fallbackPriceCents || 600);
      this.freeShippingThreshold = Number(root.dataset.freeShippingCents || 20000);
      this.shippingPrice = 3000;
      this.paypalUrl = String(root.dataset.paypalUrl || '').trim();
      this.currency = root.dataset.currency || 'USD';
      this.locale = document.documentElement.lang || 'en-US';
      this.tipCents = 0;
      this.noteUpdatePromise = Promise.resolve();
      this.search = root.querySelector('[data-wholesale-search]');
      this.grid = root.querySelector('[data-wholesale-grid]');
      this.resultCount = root.querySelector('[data-wholesale-result-count]');
      this.orderDialog = root.querySelector('[data-wholesale-order-dialog]');
      this.imageDialog = root.querySelector('[data-wholesale-image-dialog]');
      this.toast = root.querySelector('[data-wholesale-toast]');
      this.cart = null;
      this.toastTimer = null;
      this.renderCatalog();
      this.bindEvents();
      this.refreshCart();
      this.focusHashCard();
    }

    readJson(selector, fallback) {
      const node = this.root.querySelector(selector);
      if (!node) return fallback;
      try { return JSON.parse(node.textContent); } catch (error) {
        console.error('Luckich wholesale data could not be parsed.', error);
        return fallback;
      }
    }

    money(cents) {
      const value = Number(cents || 0) / 100;
      try {
        return new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.currency }).format(value);
      } catch (_) {
        return `$${value.toFixed(2)}`;
      }
    }

    variantFor(code) {
      return this.variants[code] || null;
    }

    renderCatalog() {
      if (!this.grid) return;
      this.grid.innerHTML = '';
      const fragment = document.createDocumentFragment();
      this.catalog.forEach((item) => fragment.appendChild(this.createCard(item)));
      this.grid.appendChild(fragment);
      this.updateResultCount(this.catalog.length);
    }

    createCard(item) {
      const variant = this.variantFor(item.code);
      const available = Boolean(variant && variant.id && variant.available !== false);
      const price = variant ? Number(variant.price) : this.fallbackPrice;
      const card = document.createElement('article');
      card.className = 'lk-wholesale-card';
      card.id = item.code.toUpperCase();
      card.dataset.code = item.code.toUpperCase();
      card.dataset.variantId = variant && variant.id ? String(variant.id) : '';
      card.dataset.price = String(price);
      card.dataset.size = 'M';
      card.dataset.shape = 'Shape 1';
      card.dataset.quantity = '1';
      card.innerHTML = `
        <button class="lk-wholesale-image-button" type="button" data-open-image data-full="${this.escape(item.full)}" data-code="${this.escape(item.code)}" aria-label="View large image for ${this.escape(item.code)}">
          <img src="${this.escape(item.thumb)}" alt="Luckich wholesale press-on nails ${this.escape(item.code)}" loading="lazy" decoding="async" width="500" height="625">
          <span class="lk-wholesale-zoom-label">VIEW</span>
        </button>
        <div class="lk-wholesale-card-body">
          <div class="lk-wholesale-card-top">
            <h2 class="lk-wholesale-code">${this.escape(item.code)}</h2>
            <span class="lk-wholesale-price">${this.escape(variant && variant.priceFormatted ? variant.priceFormatted : this.money(price))}</span>
          </div>
          <span class="lk-wholesale-field-label">Size</span>
          <div class="lk-wholesale-size" role="group" aria-label="Choose size for ${this.escape(item.code)}">
            ${['XS', 'S', 'M', 'L'].map((size) => `<button type="button" data-size-option data-size="${size}" aria-pressed="${size === 'M'}">${size}</button>`).join('')}
          </div>
          <span class="lk-wholesale-field-label">Quantity</span>
          <div class="lk-wholesale-purchase-row">
            <div class="lk-wholesale-quantity" aria-label="Quantity for ${this.escape(item.code)}">
              <button type="button" data-qty-change="-1" aria-label="Decrease quantity">−</button>
              <output data-quantity>1</output>
              <button type="button" data-qty-change="1" aria-label="Increase quantity">+</button>
            </div>
            <button class="lk-wholesale-add" type="button" data-add-to-order ${available ? '' : 'disabled'}>${variant && variant.available === false ? 'Sold Out' : 'Add to Order'}</button>
          </div>
          <p class="lk-wholesale-card-note">${variant ? 'Wholesale price' : 'Connect products in Theme Editor'}</p>
        </div>`;
      return card;
    }

    bindEvents() {
      this.root.addEventListener('click', (event) => {
        const clear = event.target.closest('[data-wholesale-search-clear]');
        if (clear) {
          this.search.value = '';
          this.filterCatalog('');
          this.search.focus();
          return;
        }

        const sizeButton = event.target.closest('[data-size-option]');
        if (sizeButton) {
          const card = sizeButton.closest('.lk-wholesale-card');
          card.dataset.size = sizeButton.dataset.size;
          card.querySelectorAll('[data-size-option]').forEach((button) => button.setAttribute('aria-pressed', String(button === sizeButton)));
          return;
        }

        const quantityButton = event.target.closest('[data-qty-change]');
        if (quantityButton && quantityButton.closest('.lk-wholesale-card')) {
          const card = quantityButton.closest('.lk-wholesale-card');
          const next = Math.max(1, Math.min(99, Number(card.dataset.quantity || 1) + Number(quantityButton.dataset.qtyChange)));
          card.dataset.quantity = String(next);
          card.querySelector('[data-quantity]').textContent = String(next);
          return;
        }

        const addButton = event.target.closest('[data-add-to-order]');
        if (addButton) {
          this.addCard(addButton.closest('.lk-wholesale-card'), addButton);
          return;
        }

        const imageButton = event.target.closest('[data-open-image]');
        if (imageButton) {
          this.openImage(imageButton.dataset.full, imageButton.dataset.code);
          return;
        }

        if (event.target.closest('[data-view-order]')) {
          this.openOrder();
          return;
        }

        const closeButton = event.target.closest('[data-close-dialog]');
        if (closeButton) {
          closeButton.closest('dialog')?.close();
          return;
        }

        const cartQuantity = event.target.closest('[data-cart-qty-change]');
        if (cartQuantity) {
          const line = cartQuantity.closest('[data-cart-line]');
          const next = Math.max(0, Number(line.dataset.quantity || 0) + Number(cartQuantity.dataset.cartQtyChange));
          this.changeCartLine(line.dataset.key, next);
          return;
        }

        const remove = event.target.closest('[data-remove-cart-line]');
        if (remove) {
          this.changeCartLine(remove.closest('[data-cart-line]').dataset.key, 0);
          return;
        }

        const paypal = event.target.closest('[data-wholesale-paypal]');
        if (paypal) {
          if (paypal.getAttribute('aria-disabled') === 'true') {
            event.preventDefault();
            this.showToast(this.paypalUrl ? 'Add at least one style before payment.' : 'Add your PayPal link in the Theme Editor first.', true);
            return;
          }
          this.copyOrderSummary();
          return;
        }

        const cartLink = event.target.closest('[data-wholesale-cart-link]');
        if (cartLink) {
          event.preventDefault();
          this.openCartAfterPendingUpdates(cartLink.href);
        }
      });

      this.search?.addEventListener('input', (event) => this.filterCatalog(event.target.value));
      this.root.addEventListener('change', (event) => {
        const note = event.target.closest('[data-line-note]');
        if (note) {
          this.noteUpdatePromise = this.noteUpdatePromise
            .catch(() => {})
            .then(() => this.updateLineNote(note.dataset.key, note.value));
          return;
        }

        const tip = event.target.closest('[data-tip-option]');
        if (tip) this.updateTip(Number(tip.value));
      });
      [this.orderDialog, this.imageDialog].forEach((dialog) => dialog?.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      }));
    }

    escape(value) {
      return String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }

    filterCatalog(value) {
      const query = String(value || '').trim().toUpperCase().replace(/\s+/g, '');
      let visible = 0;
      this.grid.querySelectorAll('.lk-wholesale-card').forEach((card) => {
        const matches = !query || card.dataset.code.includes(query);
        card.hidden = !matches;
        if (matches) visible += 1;
      });
      let empty = this.grid.querySelector('.lk-wholesale-empty');
      if (!visible) {
        if (!empty) {
          empty = document.createElement('div');
          empty.className = 'lk-wholesale-empty';
          this.grid.appendChild(empty);
        }
        empty.textContent = `No style found for “${value}”. Try a code such as L8035.`;
      } else if (empty) {
        empty.remove();
      }
      this.updateResultCount(visible);
    }

    updateResultCount() {
      if (this.resultCount) this.resultCount.textContent = 'WHOLESALE STYLES';
    }

    focusHashCard() {
      const code = decodeURIComponent(window.location.hash.slice(1)).trim().toUpperCase();
      if (!/^L8\d{3}$/.test(code)) return;
      const card = this.root.querySelector(`#${CSS.escape(code)}`);
      if (!card) return;
      window.setTimeout(() => {
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
        card.classList.add('is-hash-target');
        window.setTimeout(() => card.classList.remove('is-hash-target'), 2800);
      }, 180);
    }

    async addCard(card, button) {
      const variantId = Number(card.dataset.variantId);
      if (!variantId) {
        this.showToast('Wholesale products must be connected in the Theme Editor first.', true);
        return;
      }
      const original = button.textContent;
      button.disabled = true;
      button.textContent = 'Adding...';
      try {
        const code = card.dataset.code;
        const size = card.dataset.size || 'M';
        const shape = card.dataset.shape || 'Shape 1';
        const quantity = Number(card.dataset.quantity || 1);
        const price = Number(card.dataset.price || this.fallbackPrice);
        const key = `${code}|${size}|${shape}`;
        const items = this.wholesaleItems().map((item) => ({ ...item }));
        const existing = items.find((item) => item.key === key);
        if (existing) {
          existing.quantity = Number(existing.quantity || 0) + quantity;
          existing.line_price = price * existing.quantity;
          existing.final_line_price = existing.line_price;
        } else {
          items.push({
            key,
            sku: code,
            quantity,
            price,
            final_price: price,
            line_price: price * quantity,
            final_line_price: price * quantity,
            image: card.querySelector('img')?.src || '',
            properties: {
              'Style Code': code,
              'Size': size,
              'Nail Shape': shape,
              'Wholesale': 'Yes'
            }
          });
        }
        this.cart = { items };
        this.saveStoredOrder();
        this.renderOrderSummary();
        this.renderOrderLines();
        button.textContent = 'Added';
        this.showToast(`${code} · Size ${size} · ${shape} added to your order.`);
        setTimeout(() => { button.textContent = original; button.disabled = false; }, 1200);
      } catch (error) {
        button.textContent = original;
        button.disabled = false;
        this.showToast(error.message || 'Unable to add this style.', true);
      }
    }
    loadStoredOrder() {
      try {
        const value = JSON.parse(localStorage.getItem('luckich-wholesale-order-v2') || '[]');
        return Array.isArray(value) ? value : [];
      } catch (_) {
        return [];
      }
    }

    saveStoredOrder() {
      try { localStorage.setItem('luckich-wholesale-order-v2', JSON.stringify(this.wholesaleItems())); } catch (_) {}
    }

    refreshCart() {
      this.cart = { items: this.loadStoredOrder() };
      this.renderOrderSummary();
      this.renderOrderLines();
    }
    wholesaleItems() {
      return (this.cart && this.cart.items ? this.cart.items : []).filter((item) => item.properties && item.properties.Wholesale === 'Yes');
    }

    wholesaleTotals() {
      const items = this.wholesaleItems();
      const styles = new Set(items.map((item) => item.properties['Style Code'] || item.sku).filter(Boolean)).size;
      const quantity = items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      const subtotal = items.reduce((sum, item) => sum + Number(item.final_line_price ?? item.line_price ?? 0), 0);
      return { items, styles, quantity, subtotal };
    }

    renderOrderSummary() {
      const totals = this.wholesaleTotals();
      this.root.querySelectorAll('[data-summary-styles]').forEach((node) => { node.textContent = String(totals.styles); });
      this.root.querySelectorAll('[data-summary-quantity]').forEach((node) => { node.textContent = String(totals.quantity); });
      this.root.querySelectorAll('[data-summary-subtotal]').forEach((node) => { node.textContent = this.money(totals.subtotal); });
      const shippingPrice = totals.subtotal >= this.freeShippingThreshold ? 0 : this.shippingPrice;
      const total = totals.subtotal + shippingPrice + this.tipCents;
      this.root.querySelectorAll('[data-summary-total]').forEach((node) => { node.textContent = this.money(total); });
      this.root.querySelectorAll('[data-summary-tip]').forEach((node) => { node.textContent = this.money(this.tipCents); });
      this.root.querySelectorAll('[data-tip-option]').forEach((option) => { option.checked = Number(option.value) === this.tipCents; });
      const shipping = shippingPrice === 0 ? 'Free Shipping' : 'Shipping $30 USD';
      this.root.querySelectorAll('[data-summary-shipping]').forEach((node) => { node.textContent = shipping; });
    }

    renderOrderLines() {
      const container = this.root.querySelector('[data-wholesale-order-lines]');
      if (!container) return;
      const totals = this.wholesaleTotals();
      if (!totals.items.length) {
        container.innerHTML = '<div class="lk-wholesale-empty">Your wholesale order is empty. Choose styles from the catalog to begin.</div>';
        return;
      }
      container.innerHTML = totals.items.map((item) => {
        const code = item.properties['Style Code'] || item.sku || 'Wholesale Style';
        const size = item.properties.Size || 'M';
        const note = item.properties['Nail shape / style note'] || '';
        const catalogItem = this.catalog.find((entry) => entry.code === code);
        const image = catalogItem ? catalogItem.thumb : item.image;
        return `<div class="lk-wholesale-order-line" data-cart-line data-key="${this.escape(item.key)}" data-quantity="${Number(item.quantity)}">
          <img src="${this.escape(image || '')}" alt="${this.escape(code)}" loading="lazy" width="78" height="92">
          <div class="lk-wholesale-line-info"><strong>${this.escape(code)}</strong><span>Size ${this.escape(size)}</span><span>${this.money(item.final_price ?? item.price)} each</span></div>
          <div class="lk-wholesale-quantity" aria-label="Quantity for ${this.escape(code)}">
            <button type="button" data-cart-qty-change="-1" aria-label="Decrease quantity">−</button>
            <output>${Number(item.quantity)}</output>
            <button type="button" data-cart-qty-change="1" aria-label="Increase quantity">+</button>
          </div>
          <div class="lk-wholesale-line-price"><span>Subtotal</span><strong>${this.money(item.final_line_price ?? item.line_price)}</strong></div>
          <button class="lk-wholesale-remove" type="button" data-remove-cart-line aria-label="Remove ${this.escape(code)}">×</button>
          <label class="lk-wholesale-line-note">
            <span>Nail shape / style note (optional)</span>
            <textarea rows="2" maxlength="200" data-line-note data-key="${this.escape(item.key)}" placeholder="For example: Top style, long almond, left set">${this.escape(note)}</textarea>
          </label>
        </div>`;
      }).join('');
    }

    orderSummaryText() {
      const totals = this.wholesaleTotals();
      const lines = totals.items.map((item) => {
        const code = item.properties['Style Code'] || item.sku || 'Wholesale Style';
        const size = item.properties.Size || 'M';
        const note = String(item.properties['Nail shape / style note'] || '').trim();
        return `${code} | Size ${size} | Qty ${Number(item.quantity)}${note ? ` | Note: ${note}` : ''} | ${this.money(item.final_line_price ?? item.line_price)}`;
      });
      return [
        'LUCKICH WHOLESALE ORDER',
        ...lines,
        `Subtotal: ${this.money(totals.subtotal)}`,
        `Shipping: ${totals.subtotal >= this.freeShippingThreshold ? 'Free Shipping' : this.money(this.shippingPrice)}`,
        `Tip: ${this.money(this.tipCents)}`,
        `Total: ${this.money(totals.subtotal + (totals.subtotal >= this.freeShippingThreshold ? 0 : this.shippingPrice) + this.tipCents)}`
      ].join('\n');
    }

    copyOrderSummary() {
      const summary = this.orderSummaryText();
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(summary).then(() => this.showToast('Order details copied. Pay only the amount confirmed with Luckich.')).catch(() => this.copyOrderSummaryFallback(summary));
      } else {
        this.copyOrderSummaryFallback(summary);
      }
    }

    copyOrderSummaryFallback(summary) {
      const area = document.createElement('textarea');
      area.value = summary;
      area.setAttribute('readonly', '');
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      document.execCommand('copy');
      area.remove();
      this.showToast('Order details copied. Pay only the amount confirmed with Luckich.');
    }

    async changeCartLine(key, quantity) {
      const items = this.wholesaleItems().map((item) => ({ ...item }));
      const item = items.find((entry) => entry.key === key);
      if (!item) return;
      if (quantity <= 0) {
        this.cart = { items: items.filter((entry) => entry.key !== key) };
      } else {
        item.quantity = quantity;
        item.line_price = Number(item.price || 0) * quantity;
        item.final_line_price = item.line_price;
        this.cart = { items };
      }
      this.saveStoredOrder();
      this.renderOrderSummary();
      this.renderOrderLines();
    }
    openOrder() {
      this.renderOrderLines();
      this.renderOrderSummary();
      if (this.orderDialog?.showModal) this.orderDialog.showModal();
    }

    openImage(url, code) {
      const image = this.imageDialog?.querySelector('img');
      if (!image || !this.imageDialog?.showModal) return;
      image.src = url;
      image.alt = `Luckich wholesale press-on nails ${code}`;
      this.imageDialog.showModal();
    }

    async addCard(card, button) {
      const variantId = Number(card.dataset.variantId);
      if (!variantId) {
        this.showToast('This wholesale variant is not available on the storefront.', true);
        return;
      }
      const original = button.textContent;
      button.disabled = true;
      button.textContent = 'Adding...';
      try {
        const code = card.dataset.code;
        const size = card.dataset.size || 'M';
        const quantity = Number(card.dataset.quantity || 1);
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{
              id: variantId,
              quantity,
              properties: { 'Style Code': code, Size: size, Wholesale: 'Yes' }
            }]
          })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'Unable to add this style to cart.');
        await this.refreshCart();
        button.textContent = 'Added';
        this.showToast(`${code} | Size ${size} added to your Shopify cart.`);
        window.setTimeout(() => { button.textContent = original; button.disabled = false; }, 1200);
      } catch (error) {
        button.textContent = original;
        button.disabled = false;
        this.showToast(error.message || 'Unable to add this style.', true);
      }
    }

    async refreshCart() {
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Unable to load the Shopify cart.');
        this.cart = await response.json();
        this.tipCents = this.tipFromCart(this.cart);
        this.renderOrderSummary();
        this.renderOrderLines();
      } catch (error) {
        this.cart = { items: [] };
        this.showToast(error.message, true);
      }
    }

    async changeCartLine(key, quantity) {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      const result = await response.json();
      if (!response.ok) {
        this.showToast(result.description || 'Unable to update the Shopify cart.', true);
        return;
      }
      this.cart = result;
      this.tipCents = this.tipFromCart(this.cart);
      this.renderOrderSummary();
      this.renderOrderLines();
    }

    tipFromCart(cart) {
      const cents = Math.round(Number(cart?.attributes?.['Wholesale Tip'] || 0) * 100);
      return [0, 500, 1000, 2000, 5000].includes(cents) ? cents : 0;
    }

    async updateTip(tipCents) {
      const nextTip = [0, 500, 1000, 2000, 5000].includes(tipCents) ? tipCents : 0;
      const previousTip = this.tipCents;
      this.tipCents = nextTip;
      this.renderOrderSummary();
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: { 'Wholesale Tip': String(nextTip / 100) } })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'Unable to save the tip.');
        this.cart = result;
        this.tipCents = this.tipFromCart(result);
        this.renderOrderSummary();
      } catch (error) {
        this.tipCents = previousTip;
        this.renderOrderSummary();
        this.showToast(error.message || 'Unable to save the tip.', true);
      }
    }

    async updateLineNote(key, value) {
      const item = this.wholesaleItems().find((entry) => entry.key === key);
      if (!item) return;
      const note = String(value || '').trim().slice(0, 200);
      const currentNote = String(item.properties?.['Nail shape / style note'] || '').trim();
      if (note === currentNote) return;
      const properties = { ...(item.properties || {}) };
      if (note) properties['Nail shape / style note'] = note;
      else delete properties['Nail shape / style note'];
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: Number(item.quantity || 1), properties })
      });
      const result = await response.json();
      if (!response.ok) {
        this.showToast(result.description || 'Unable to save the nail style note.', true);
        return;
      }
      this.cart = result;
      this.tipCents = this.tipFromCart(result);
      this.renderOrderSummary();
      this.renderOrderLines();
    }

    async openCartAfterPendingUpdates(url) {
      await this.noteUpdatePromise.catch(() => {});
      window.location.assign(url);
    }

    showToast(message, isError = false) {
      if (!this.toast) return;
      clearTimeout(this.toastTimer);
      this.toast.textContent = message;
      this.toast.classList.toggle('is-error', isError);
      this.toast.classList.add('is-visible');
      this.toastTimer = setTimeout(() => this.toast.classList.remove('is-visible'), 3200);
    }
  }

  function init(scope = document) {
    const roots = [];
    if (scope.matches && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    roots.push(...scope.querySelectorAll(ROOT_SELECTOR));
    roots.forEach((root) => {
      if (root.dataset.lkWholesaleReady === 'true') return;
      root.dataset.lkWholesaleReady = 'true';
      new LuckichWholesaleCatalog(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
